"use strict";
/**
 * ADR-066 Counter Master — the shared vocabulary for what a cash counter
 * is, which workstation bills at it, and how often its cash must leave.
 *
 * These three lists exist here rather than being re-declared on each
 * side because the backend enforces them (Prisma enums + Zod) and the
 * frontend renders them (dropdowns, grid labels), and a counter typed
 * `MAIN_COUNTER` on one side and `Main Counter` on the other is exactly
 * the drift that makes a settlement rule unenforceable. The string
 * values are identical to the Postgres enum members in
 * `20260804120000_adr066_counter_master`; the labels are the only thing
 * a user ever sees.
 *
 * `COUNTER_WORKSTATION_ROUTES` maps each workstation to the route that
 * actually exists today in `frontend/src/config/navigation-registry.ts`.
 * It is a real mapping, not a catalog of intentions — if a workstation
 * has no route, it does not belong in this enum.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.CASH_DEPOSIT_FREQUENCY_LABELS = exports.CASH_DEPOSIT_FREQUENCIES = exports.COUNTER_WORKSTATION_ROUTES = exports.COUNTER_WORKSTATION_LABELS = exports.COUNTER_WORKSTATIONS = exports.COUNTER_TYPE_LABELS = exports.COUNTER_TYPES = void 0;
exports.counterRequiresParent = counterRequiresParent;
exports.COUNTER_TYPES = [
    'REGISTRATION',
    'OP_BILLING',
    'IP_BILLING',
    'EMERGENCY',
    'PHARMACY',
    'LABORATORY',
    'RADIOLOGY',
    'PROCEDURE',
    'CORPORATE_BILLING',
    'CASH_COLLECTION',
    'REFUND',
    'NIGHT',
    'MAIN_COUNTER',
    'MOBILE',
    'CAMP',
];
exports.COUNTER_TYPE_LABELS = {
    REGISTRATION: 'Registration Counter',
    OP_BILLING: 'OP Billing Counter',
    IP_BILLING: 'IP Billing Counter',
    EMERGENCY: 'Emergency Counter',
    PHARMACY: 'Pharmacy Counter',
    LABORATORY: 'Laboratory Counter',
    RADIOLOGY: 'Radiology Counter',
    PROCEDURE: 'Procedure Counter',
    CORPORATE_BILLING: 'Corporate Billing Counter',
    CASH_COLLECTION: 'Cash Collection Counter',
    REFUND: 'Refund Counter',
    NIGHT: 'Night Counter',
    MAIN_COUNTER: 'Main Counter',
    MOBILE: 'Mobile Counter',
    CAMP: 'Camp Counter',
};
exports.COUNTER_WORKSTATIONS = [
    'NONE',
    'FRONT_DESK',
    'APPOINTMENT',
    'OP_BILLING',
    'IP_BILLING',
    'PHARMACY_BILLING',
    'LABORATORY_BILLING',
    'RADIOLOGY_BILLING',
    'PACKAGE_BILLING',
    'REFUND_CREDIT_NOTE',
    'FINANCE_CASH_BANK',
];
exports.COUNTER_WORKSTATION_LABELS = {
    NONE: 'None (receives cash only)',
    FRONT_DESK: 'Front Desk Workstation',
    APPOINTMENT: 'Appointment Workstation',
    OP_BILLING: 'OP Billing',
    IP_BILLING: 'IP Billing',
    PHARMACY_BILLING: 'Pharmacy Billing',
    LABORATORY_BILLING: 'Laboratory Billing',
    RADIOLOGY_BILLING: 'Radiology Billing',
    PACKAGE_BILLING: 'Package Billing',
    REFUND_CREDIT_NOTE: 'Refunds & Credit Notes',
    FINANCE_CASH_BANK: 'Finance — Cash & Bank',
};
/** Where an operator at this counter actually bills. `NONE` has no route by definition. */
exports.COUNTER_WORKSTATION_ROUTES = {
    NONE: null,
    FRONT_DESK: '/patients/workstation',
    APPOINTMENT: '/appointments',
    OP_BILLING: '/op-billing',
    IP_BILLING: '/billing/ip',
    PHARMACY_BILLING: '/pharmacy-billing/workstation',
    LABORATORY_BILLING: '/billing/laboratory',
    RADIOLOGY_BILLING: '/billing/radiology',
    PACKAGE_BILLING: '/billing/packages',
    REFUND_CREDIT_NOTE: '/billing/refunds',
    FINANCE_CASH_BANK: '/finance?tab=cash-book',
};
exports.CASH_DEPOSIT_FREQUENCIES = ['PER_SHIFT', 'DAILY', 'WEEKLY', 'ON_DEMAND'];
exports.CASH_DEPOSIT_FREQUENCY_LABELS = {
    PER_SHIFT: 'Every shift close',
    DAILY: 'Daily',
    WEEKLY: 'Weekly',
    ON_DEMAND: 'On demand',
};
/**
 * The one structural rule of the counter hierarchy: a Main Counter is
 * the root that receives settlements, so it has no parent; every other
 * counter submits to exactly one Main Counter. Enforced by
 * `CashCounterService` and mirrored by the form so the UI cannot ask
 * for a parent the API would reject.
 */
function counterRequiresParent(counterType) {
    return counterType !== 'MAIN_COUNTER';
}
//# sourceMappingURL=counter.js.map